import React, { useRef, useEffect, useState, useCallback } from "react";
import { FaBold, FaItalic, FaUnderline, FaAlignLeft, FaAlignCenter, FaAlignRight, FaImage, FaEraser } from "react-icons/fa";
import "./HeaderFooterEditor.scss";

// A4 width at 96 DPI - MUST stay in sync with IMAGE_WIDTH_PX in InvoiceTemplateManager.js
// so that what the user designs here is pixel-accurate to the real printed invoice.
const CANVAS_WIDTH_PX = 794;

// Returns true when an HTML string produced by the editor has no visible content
// (an inserted logo counts as content even though it has no text).
export const isHtmlContentEmpty = (html) => {
    if (!html) return true;
    const withoutImages = html.replace(/<img[^>]*>/gi, "§HAS_IMAGE§");
    const stripped = withoutImages
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .trim();
    return stripped.length === 0;
};

const FONT_FAMILIES = [
    { label: "Arial (Default)", value: "Arial, Helvetica, sans-serif" },
    { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
    { label: "Georgia", value: "Georgia, serif" },
    { label: "Courier New", value: "'Courier New', Courier, monospace" },
    { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
];

const FONT_SIZES = [
    { label: "Default", value: "8px" },
    { label: "Normal", value: "12px" },
    { label: "Large", value: "18px" },
    { label: "X-Large", value: "24px" },
];

// Strips surrounding quotes ("Arial" -> Arial) and keeps only the first token of a
// font stack, so a value from FONT_FAMILIES can be compared against whatever
// document.queryCommandValue("fontName") reports for the browser/font in question.
const normalizeFontToken = (value) =>
    (value || "").split(",")[0].replace(/^["']+|["']+$/g, "").trim().toLowerCase();

const getFirstTextElement = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
        if (node.textContent.trim()) return node.parentElement || root;
        node = walker.nextNode();
    }
    return root;
};

const getSavedFontSelections = (root) => {
    const element = getFirstTextElement(root);
    const computedStyle = window.getComputedStyle(element);
    const font = FONT_FAMILIES.find(
        (item) => normalizeFontToken(item.value) === normalizeFontToken(computedStyle.fontFamily)
    );
    const fontSize = FONT_SIZES.find(
        (item) => parseFloat(item.value) === parseFloat(computedStyle.fontSize)
    );
    return {
        font: font ? font.value : FONT_FAMILIES[0].value,
        fontSize: fontSize ? fontSize.value : FONT_SIZES[0].value,
    };
};

/**
 * Rich-text header/footer designer.
 * Lets the user format text (bold / italic / underline / font / size / color / align)
 * and insert a logo image inline, like a Word document header or footer,
 * while keeping the exact same height restriction used for uploaded images.
 *
 * Built on a plain contentEditable div + document.execCommand rather than a
 * third-party editor library, so it has no external runtime dependency to install.
 */
const HeaderFooterEditor = ({
    label,
    value,
    onChange,
    heightPx,
    onOverflowChange,
    onUploadLogo,
    placeholder,
}) => {
    const editableRef = useRef(null);
    const fileInputRef = useRef(null);
    const savedRangeRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [contentHeight, setContentHeight] = useState(0);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);
    const [activeFormats, setActiveFormats] = useState({ bold: false, italic: false, underline: false });
    const [selectedFont, setSelectedFont] = useState(FONT_FAMILIES[0].value);
    const [selectedFontSize, setSelectedFontSize] = useState(FONT_SIZES[0].value);

    const measureOverflow = useCallback(() => {
        const el = editableRef.current;
        if (!el) return;
        const actualHeight = el.scrollHeight;
        setContentHeight(actualHeight);
        const overflowing = actualHeight > heightPx + 2; // small tolerance for rounding
        setIsOverflowing(overflowing);
        onOverflowChange && onOverflowChange(overflowing);
    }, [heightPx, onOverflowChange]);

    // Sync external value into the DOM only when it actually differs from what's
    // already there (e.g. switching to edit an existing template) - never while the
    // user is mid-keystroke, or the caret would jump back to the start every input.
    useEffect(() => {
        const el = editableRef.current;
        if (!el) return;
        const nextHtml = value || "";
        if (el.innerHTML !== nextHtml) {
            el.innerHTML = nextHtml;
            const savedSelections = getSavedFontSelections(el);
            setSelectedFont(savedSelections.font);
            setSelectedFontSize(savedSelections.fontSize);
        }
        setIsEmpty(isHtmlContentEmpty(nextHtml));
        const t = setTimeout(measureOverflow, 80);
        return () => clearTimeout(t);
    }, [value]);

    const syncActiveFormats = useCallback(() => {
        setActiveFormats({
            bold: document.queryCommandState("bold"),
            italic: document.queryCommandState("italic"),
            underline: document.queryCommandState("underline"),
        });
        try {
            const currentFont = normalizeFontToken(document.queryCommandValue("fontName"));
            const match = currentFont && FONT_FAMILIES.find((f) => normalizeFontToken(f.value) === currentFont);
            setSelectedFont(match ? match.value : FONT_FAMILIES[0].value);

            const selection = window.getSelection();
            const anchorElement = selection?.anchorNode?.nodeType === Node.ELEMENT_NODE
                ? selection.anchorNode
                : selection?.anchorNode?.parentElement;
            if (anchorElement && editableRef.current?.contains(anchorElement)) {
                const currentSize = window.getComputedStyle(anchorElement).fontSize;
                const sizeMatch = FONT_SIZES.find(
                    (item) => parseFloat(item.value) === parseFloat(currentSize)
                );
                setSelectedFontSize(sizeMatch ? sizeMatch.value : FONT_SIZES[0].value);
            }
        } catch (err) {
            // queryCommandValue("fontName") isn't supported in every browser - leave as-is
        }
    }, []);

    // Keep the toolbar's bold/italic/underline state in sync with wherever the
    // caret currently is, not just with the last button clicked.
    useEffect(() => {
        const handler = () => {
            const el = editableRef.current;
            const sel = window.getSelection();
            if (el && sel && sel.rangeCount && el.contains(sel.anchorNode)) {
                syncActiveFormats();
            }
        };
        document.addEventListener("selectionchange", handler);
        return () => document.removeEventListener("selectionchange", handler);
    }, [syncActiveFormats]);

    const emitChange = useCallback(() => {
        const el = editableRef.current;
        if (!el) return;
        const html = el.innerHTML === "<br>" ? "" : el.innerHTML;
        onChange(html);
        setIsEmpty(isHtmlContentEmpty(html));
        measureOverflow();
    }, [onChange, measureOverflow]);

    // The toolbar steals focus from the editable div on click, which collapses the
    // browser's text selection - so we save/restore it ourselves around every command.
    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0 && editableRef.current?.contains(sel.anchorNode)) {
            savedRangeRef.current = sel.getRangeAt(0);
        }
    };

    const restoreSelection = () => {
        const sel = window.getSelection();
        if (!sel || !savedRangeRef.current) return;
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
    };

    const focusEditor = () => {
        editableRef.current?.focus();
        restoreSelection();
    };

    const exec = (command, arg = null) => {
        focusEditor();
        document.execCommand(command, false, arg);
        syncActiveFormats();
        emitChange();
    };

    const applyFontSize = (px) => {
        if (!px) return;
        setSelectedFontSize(px);
        focusEditor();
        // execCommand("fontSize") only accepts the legacy sizes 1-7, so apply a
        // sentinel size and swap it out for the real pixel value afterwards.
        document.execCommand("fontSize", false, "7");
        const el = editableRef.current;
        if (el) {
            el.querySelectorAll("font[size=\"7\"]").forEach((node) => {
                node.removeAttribute("size");
                node.style.fontSize = px;
            });
        }
        emitChange();
    };

    const handleImageButtonClick = () => {
        saveSelection();
        fileInputRef.current && fileInputRef.current.click();
    };

    const handleLogoSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            return;
        }
        if (file.size > 1 * 1024 * 1024) {
            window.alert("Logo image should be less than 1MB");
            return;
        }

        try {
            setUploadingLogo(true);
            const src = onUploadLogo
                ? await onUploadLogo(file)
                : await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });

            focusEditor();
            document.execCommand(
                "insertHTML",
                false,
                `<img src="${src}" alt="Logo" style="max-height:${heightPx}px;max-width:100%;vertical-align:middle;" />`
            );
            emitChange();
        } catch (err) {
            window.alert("Failed to insert logo. Please try again.");
        } finally {
            setUploadingLogo(false);
        }
    };

    // Strip formatting from pasted content so external styles/markup (e.g. from
    // Word or a web page) can't blow past the fixed print-accurate canvas.
    const handlePaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        document.execCommand("insertText", false, text);
        emitChange();
    };

    return (
        <div className="hfe-wrap">
            <div className="hfe-label-row">
                <span className="hfe-label">{label}</span>
                <span className={`hfe-size-tag ${isOverflowing ? "hfe-size-tag--danger" : ""}`}>
                    {contentHeight}px / {heightPx}px max
                </span>
            </div>

            <div
                className="hfe-editor-shell"
                style={{
                    "--hfe-height": `${heightPx}px`,
                    "--hfe-width": `${CANVAS_WIDTH_PX}px`,
                    "--hfe-default-font-size": selectedFontSize,
                    "--hfe-placeholder-font-size": selectedFontSize,
                }}
            >
                <div className="hfe-toolbar">
                    <span className="hfe-toolbar-group">
                        <select
                            className="hfe-select"
                            value={selectedFont}
                            title="Font"
                            onMouseDown={saveSelection}
                            onChange={(e) => {
                                setSelectedFont(e.target.value);
                                if (e.target.value) exec("fontName", e.target.value);
                            }}
                        >
                            {FONT_FAMILIES.map((f) => (
                                <option key={f.label} value={f.value}>{f.label}</option>
                            ))}
                        </select>

                        <select
                            className="hfe-select"
                            value={selectedFontSize}
                            title="Size"
                            onMouseDown={saveSelection}
                            onChange={(e) => applyFontSize(e.target.value)}
                        >
                            {FONT_SIZES.map((s) => (
                                <option key={s.label} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </span>

                    <span className="hfe-toolbar-group">
                        <button
                            type="button"
                            className={`hfe-btn ${activeFormats.bold ? "hfe-btn--active" : ""}`}
                            aria-label="Bold"
                            title="Bold"
                            onClick={() => exec("bold")}
                        >
                            <FaBold />
                        </button>
                        <button
                            type="button"
                            className={`hfe-btn ${activeFormats.italic ? "hfe-btn--active" : ""}`}
                            aria-label="Italic"
                            title="Italic"
                            onClick={() => exec("italic")}
                        >
                            <FaItalic />
                        </button>
                        <button
                            type="button"
                            className={`hfe-btn ${activeFormats.underline ? "hfe-btn--active" : ""}`}
                            aria-label="Underline"
                            title="Underline"
                            onClick={() => exec("underline")}
                        >
                            <FaUnderline />
                        </button>
                        <input
                            type="color"
                            className="hfe-color-input"
                            title="Text color"
                            onMouseDown={saveSelection}
                            onChange={(e) => exec("foreColor", e.target.value)}
                        />
                    </span>

                    <span className="hfe-toolbar-group">
                        <button type="button" className="hfe-btn" aria-label="Align left" title="Align left" onClick={() => exec("justifyLeft")}>
                            <FaAlignLeft />
                        </button>
                        <button type="button" className="hfe-btn" aria-label="Align center" title="Align center" onClick={() => exec("justifyCenter")}>
                            <FaAlignCenter />
                        </button>
                        <button type="button" className="hfe-btn" aria-label="Align right" title="Align right" onClick={() => exec("justifyRight")}>
                            <FaAlignRight />
                        </button>
                    </span>

                    <span className="hfe-toolbar-group">
                        <button type="button" className="hfe-btn" aria-label="Insert Logo" title="Insert Logo" onClick={handleImageButtonClick}>
                            <FaImage />
                        </button>
                        <button type="button" className="hfe-btn" aria-label="Clear Formatting" title="Clear Formatting" onClick={() => exec("removeFormat")}>
                            <FaEraser />
                        </button>
                    </span>
                </div>

                <div
                    ref={editableRef}
                    className={`hfe-editable ${isEmpty ? "hfe-editable--empty" : ""}`}
                    contentEditable
                    suppressContentEditableWarning
                    data-placeholder={placeholder}
                    onInput={emitChange}
                    onBlur={() => { saveSelection(); emitChange(); }}
                    onKeyUp={syncActiveFormats}
                    onMouseUp={syncActiveFormats}
                    onPaste={handlePaste}
                />
            </div>

            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleLogoSelected}
            />

            {uploadingLogo && <div className="hfe-uploading">Uploading logo…</div>}

            {isOverflowing && (
                <div className="hfe-overflow-warning">
                    <strong>⚠ Too tall.</strong> This {label.toLowerCase()} content is taller than the allowed
                    area and will be cut off when the invoice is printed. Shorten the text, use a smaller
                    font size, or reduce the logo size.
                </div>
            )}
        </div>
    );
};

export default HeaderFooterEditor;
